import type { LoadTranslationsQuery } from '#build/graphql-operations'
import { defineEasyTextsLoader } from 'nuxt-easy-texts/loader'

type MappedTranslations = Record<string, string | [string, string]>

function getTranslations(v?: LoadTranslationsQuery): MappedTranslations {
  if (!v) {
    return {}
  }
  return Object.entries(
    (v.translations || {}) as Record<
      string,
      string | { singular?: string; plural?: string }
    >,
  ).reduce<Record<string, string | [string, string]>>(
    (acc, [fullKey, value]) => {
      const keyWithDots = fullKey.replace('__', '.')
      if (typeof value === 'string') {
        acc[keyWithDots] = value
      } else if (typeof value === 'object' && value.plural && value.singular) {
        acc[keyWithDots] = [value.singular, value.plural]
      }
      return acc
    },
    {},
  )
}

export default defineEasyTextsLoader(() => {
  const language = useCurrentLanguage()
  const event = useRequestEvent()
  return {
    async load() {
      const { value, addToCache } = await useDataCache<MappedTranslations>(
        'translations_' + language.value,
        event,
      )
      if (value) {
        return value
      }

      const data = await useGraphqlQuery('loadTranslations', null, {
        clientContext: {
          l: language.value,
        },
        graphqlCaching: {
          client: true,
        },
      })

      const mapped = getTranslations(data.data)
      await addToCache(mapped, data.extensions?.cacheability?.tagsNuxt, 60 * 60)
      return mapped
    },
    reloadTrigger() {
      return computed(() => language.value)
    },
  }
})
